import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';

const FinanceCalculator = () => {
  const [treatmentCost, setTreatmentCost] = useState(2500);
  const [selectedMonths, setSelectedMonths] = useState(12);

  const monthOptions = [3, 6, 9, 12, 24, 60];
  const monthlyPayment = treatmentCost / selectedMonths;
  const apr = 0; // 0% interest-free
  const costOfCredit = 0;
  const totalPayback = treatmentCost;

  const handleCostChange = (e) => {
    const value = e.target.value.replace(/[£,]/g, '');
    const numValue = parseFloat(value) || 0;
    setTreatmentCost(numValue);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Calculator Card */}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
        
        {/* Top Section - Treatment Cost Input */}
        <div className="bg-gradient-to-r from-burnt-coral to-golden-sand p-8 md:p-12">
          <label className="block text-white text-sm font-semibold mb-3 uppercase tracking-wider">
            Total Treatment Cost
          </label>
          <div className="relative">
            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-white text-3xl md:text-4xl font-bold">
              £
            </span>
            <input
              type="text"
              value={treatmentCost.toLocaleString('en-GB')}
              onChange={handleCostChange}
              className="w-full bg-white/20 border-b-4 border-white text-white text-4xl md:text-6xl font-bold py-4 pl-16 pr-6 focus:outline-none focus:bg-white/30 transition-all placeholder-white/60"
              placeholder="2500"
            />
          </div>
        </div>

        {/* Middle Section - Payment Plan Dropdown */}
        <div className="bg-gradient-to-r from-burnt-coral/90 to-golden-sand/90 p-8 md:p-12">
          <label className="block text-white text-sm font-semibold mb-4 uppercase tracking-wider">
            Number of Payments
          </label>
          <select
            value={selectedMonths}
            onChange={(e) => setSelectedMonths(parseInt(e.target.value))}
            className="w-full bg-white text-burnt-coral text-2xl md:text-3xl font-bold py-4 px-6 rounded-xl focus:outline-none focus:ring-4 focus:ring-white/50 cursor-pointer transition-all hover:scale-[1.02]"
          >
            {monthOptions.map((months) => (
              <option key={months} value={months} className="text-2xl">
                {months} {months === 1 ? 'month' : 'months'}
              </option>
            ))}
          </select>
        </div>

        {/* Bottom Section - Results */}
        <div className="bg-rabbit p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            
            {/* Left - Monthly Payment Circle */}
            <div className="flex justify-center">
              <div className="relative w-64 h-64">
                {/* Circle background */}
                <svg className="w-full h-full -rotate-90">
                  <circle
                    cx="128"
                    cy="128"
                    r="110"
                    fill="none"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="20"
                  />
                  <circle
                    cx="128"
                    cy="128"
                    r="110"
                    fill="none"
                    stroke="url(#gradient)"
                    strokeWidth="20"
                    strokeDasharray="691.15"
                    strokeDashoffset="0"
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#F5A962" />
                      <stop offset="100%" stopColor="#FF6B6B" />
                    </linearGradient>
                  </defs>
                </svg>
                
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-golden-sand text-5xl font-bold mb-1">
                    {formatCurrency(monthlyPayment)}
                  </div>
                  <div className="text-white text-sm uppercase tracking-wider font-semibold">
                    Per Month
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Breakdown */}
            <div className="space-y-6">
              <div className="flex justify-between items-center pb-4 border-b border-white/20">
                <span className="text-white/80 text-lg uppercase tracking-wide">Interest (APR)</span>
                <span className="text-white text-2xl font-bold">{apr}% (0.0%)</span>
              </div>
              
              <div className="flex justify-between items-center pb-4 border-b border-white/20">
                <span className="text-white/80 text-lg uppercase tracking-wide">Cost of Credit</span>
                <span className="text-white text-2xl font-bold">{formatCurrency(costOfCredit)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-white/80 text-lg uppercase tracking-wide">Total to Pay Back</span>
                <span className="text-golden-sand text-3xl font-bold">{formatCurrency(totalPayback)}</span>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 pt-8 border-t border-white/20">
            <p className="text-white/60 text-sm mb-3">
              *Rates are indicative and for illustrative purposes only
            </p>
            <p className="text-white/70 text-base">
              For more information on our finance provider, please visit{' '}
              <a 
                href="https://www.tabeo.co.uk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-golden-sand hover:text-burnt-coral underline font-semibold transition-colors"
              >
                Tabeo
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Additional Info Cards */}
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
          <div className="text-burnt-coral text-4xl font-bold mb-3">0%</div>
          <h3 className="text-rabbit font-bold text-xl mb-2">Interest-Free Finance</h3>
          <p className="text-chocolate-malt text-sm">
            Pay nothing extra. What you see is what you pay.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
          <div className="text-burnt-coral text-4xl font-bold mb-3">2 min</div>
          <h3 className="text-rabbit font-bold text-xl mb-2">Quick Application</h3>
          <p className="text-chocolate-malt text-sm">
            Fast, simple application with instant decision.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
          <div className="text-burnt-coral text-4xl font-bold mb-3">£30k</div>
          <h3 className="text-rabbit font-bold text-xl mb-2">Borrow Up To</h3>
          <p className="text-chocolate-malt text-sm">
            Finance available for treatments up to £30,000.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FinanceCalculator;
